/**
 * The publish dialog (plan docs/plans/lms-plan.md 8.1, R11; 18.17; WP-44):
 * it requires the change summary from version 2 on, shows the number of
 * people who hold a valid completion and the number with an open assignment
 * (the draft's publish impact), and asks the retrain policy with the
 * consequence of each spelled out. Version 1 can only wait for renewal,
 * because nobody holds it yet (the server coerces it, 18.17). A draft the
 * lint refuses comes back 400 LMS_CONTENT_INVALID with { lint }; the dialog
 * shows that list and publishes nothing.
 */
import { useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import Modal from '../../../../ui/Modal';
import { primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { apiErrorCode, apiErrorMessage } from '../../../api/client';
import { publishVersion } from '../../../api/catalog';
import type { LintResult, PublishBody, PublishImpact, PublishResult, RetrainPolicy } from '../../../api/catalog';
import { noticeCls } from '../../../components/player/styles';
import { NumberInput, TextArea } from './fields';
import { LintPanel } from './LintPanel';
import { POLICY_ORDER, POLICY_TEXT } from './model';
import { checkboxCls, hintCls, labelCls, microLabelCls } from './styles';

export interface PublishDialogProps {
  isOpen: boolean;
  onClose: () => void;
  versionId: string;
  versionNo: number;
  courseTitle: string;
  impact: PublishImpact | null;
  /** The draft's saved change summary (the dialog starts from it). */
  changeSummary: string | null;
  /** Save anything unsaved before publishing; false stops the publish. */
  beforePublish: () => Promise<boolean>;
  onPublished: (result: PublishResult) => void;
  /** The server's lint when it refused the publish (the editor shows it too). */
  onLintRefused: (lint: LintResult) => void;
}

function Count({ value, label, testId }: { value: number | null; label: string; testId: string }) {
  return (
    <div className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
      <div className="text-xl font-bold tabular-nums text-gray-900 dark:text-gray-100" data-testid={testId}>
        {value === null ? '\u2014' : value.toLocaleString()}
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );
}

export function PublishDialog(props: PublishDialogProps) {
  const { isOpen, onClose, versionId, versionNo, courseTitle, impact } = props;
  const firstVersion = versionNo <= 1;
  // Version 1 has one policy; from version 2 the admin chooses (8.1: "asks the retrain policy"), nothing is preselected.
  const [policy, setPolicy] = useState<RetrainPolicy | null>(firstVersion ? 'at_renewal' : null);
  const [summary, setSummary] = useState(props.changeSummary ?? '');
  const [dueDays, setDueDays] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState<LintResult | null>(null);

  const summaryMissing = !firstVersion && !summary.trim();
  const policyMissing = !firstVersion && policy === null;
  const needsDueDays = policy === 'immediate' || policy === 'acknowledge';
  const policies = firstVersion ? (['at_renewal'] as RetrainPolicy[]) : POLICY_ORDER;

  const publish = async () => {
    if (busy || summaryMissing || policyMissing) return;
    setBusy(true);
    setError(null);
    setRefused(null);
    try {
      const saved = await props.beforePublish();
      if (!saved) {
        setError('Your latest edits could not be saved, so nothing was published. Try again once the draft saves.');
        return;
      }
      const body: PublishBody = { retrain_policy: firstVersion || policy === null ? 'at_renewal' : policy };
      if (!firstVersion) body.change_summary = summary.trim();
      else if (summary.trim()) body.change_summary = summary.trim();
      if (!firstVersion && needsDueDays && dueDays !== null) body.retrain_due_days = dueDays;
      const result = await publishVersion(versionId, body);
      props.onPublished(result);
    } catch (err) {
      const data = (err as { response?: { data?: { lint?: LintResult } } })?.response?.data;
      if (apiErrorCode(err) === 'LMS_CONTENT_INVALID' && data?.lint) {
        setRefused(data.lint);
        props.onLintRefused(data.lint);
      }
      setError(apiErrorMessage(err, 'The version could not be published.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onClose} title={`Publish version ${versionNo}`} maxWidth="max-w-2xl">
      <div className="space-y-4" data-testid="lms-publish-dialog">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          {`Publishing makes version ${versionNo} of "${courseTitle}" the one learners take. A published version never changes (G7); a later change is a new version.`}
        </p>

        <div className="grid gap-3 sm:grid-cols-2" aria-label="Who this publish touches">
          <Count value={impact ? impact.valid_completions : null} label="people hold a valid completion" testId="lms-publish-holders" />
          <Count value={impact ? impact.open_assignments : null} label="people have an open assignment" testId="lms-publish-open" />
        </div>

        <TextArea
          label={firstVersion ? 'Change summary (optional for version 1)' : 'Change summary (required)'}
          value={summary}
          onChange={setSummary}
          rows={3}
          testId="lms-publish-summary"
          hint="What changed and why, in plain words. Learners see it first on a retrain or an acknowledgement (6.1)."
        />

        <fieldset className="space-y-2" data-testid="lms-publish-policies">
          <legend className={labelCls}>What happens to people who already hold this course</legend>
          {firstVersion ? (
            <p className={hintCls} data-testid="lms-publish-v1-note">
              Version 1 always waits for renewal: nobody holds this course yet (8.1).
            </p>
          ) : null}
          {policies.map((p) => {
            const text = POLICY_TEXT[p];
            const checked = (firstVersion ? 'at_renewal' : policy) === p;
            return (
              <label
                key={p}
                className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors duration-150 ${
                  checked
                    ? 'border-primary-600 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20'
                    : 'border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50'
                }`}
                data-testid={`lms-policy-${p}`}
              >
                <input
                  type="radio"
                  name="lms-retrain-policy"
                  value={p}
                  checked={checked}
                  onChange={() => setPolicy(p)}
                  disabled={firstVersion}
                  className={`${checkboxCls} mt-0.5`}
                  data-testid={`lms-policy-${p}-input`}
                />
                <span className="space-y-0.5">
                  <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">{text.label}</span>
                  <span className="block text-sm text-gray-600 dark:text-gray-300">{text.sentence}</span>
                </span>
              </label>
            );
          })}
        </fieldset>

        {!firstVersion && needsDueDays ? (
          <NumberInput
            className="max-w-xs"
            label="Due in (days)"
            value={dueDays}
            onChange={setDueDays}
            min={1}
            max={365}
            allowEmpty
            placeholder="Network default"
            testId="lms-publish-due-days"
            hint="Empty uses the network's default due window (5.8)."
          />
        ) : null}

        {refused ? <LintPanel lint={refused} /> : null}
        {error ? (
          <div className={noticeCls('red')} role="alert" data-testid="lms-publish-error">
            {error}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          {summaryMissing || policyMissing ? (
            <span className={`${microLabelCls} mr-auto`} data-testid="lms-publish-missing">
              {summaryMissing ? 'Write the change summary to publish' : 'Choose what happens to current holders'}
            </span>
          ) : null}
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={primaryBtnCls} onClick={publish} disabled={busy || summaryMissing || policyMissing} data-testid="lms-publish-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
            {`Publish version ${versionNo}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default PublishDialog;
